import React from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Camera, ArrowLeft, Calendar, Check, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const BookingFlow = () => {
  const { photographerId } = useParams();
  const [searchParams] = useSearchParams();
  const preselectedPackage = searchParams.get('package');

  const [step, setStep] = React.useState(1); // 1: Package, 2: Date, 3: Details, 4: Payment
  const [selectedPackage, setSelectedPackage] = React.useState(preselectedPackage || null);
  const [selectedDate, setSelectedDate] = React.useState('');

  const photographer = {
    name: 'Sarah Johnson',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
  };

  const packages = [
    {
      name: 'essentials',
      displayName: 'Essentials',
      price: 2500,
      duration: '6 hours',
      features: ['Up to 6 hours coverage', '300+ edited photos', 'Online gallery', '1 photographer'],
    },
    {
      name: 'premium',
      displayName: 'Premium',
      price: 4500,
      duration: '10 hours',
      features: ['Up to 10 hours coverage', '600+ edited photos', 'Online gallery', '2 photographers', 'Engagement shoot'],
      popular: true,
    },
    {
      name: 'ultimate',
      displayName: 'Ultimate',
      price: 7000,
      duration: 'Full day',
      features: ['Unlimited coverage', '1000+ edited photos', 'Premium albums', '2 photographers', 'Engagement shoot', 'Same-day preview'],
    },
  ];

  const handleNext = () => {
    if (step < 4) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const selectedPkg = packages.find(p => p.name === selectedPackage);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-background sticky top-0 z-50">
        <div className="px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
              <Camera className="w-8 h-8" />
              <span className="font-playfair text-2xl font-bold">Pixnest</span>
            </Link>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        <Link
          to={`/photographer/${photographerId}`}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Profile
        </Link>

        {/* Progress Steps */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex items-center justify-between" data-testid="progress-steps">
            {[{ num: 1, label: 'Package' }, { num: 2, label: 'Date' }, { num: 3, label: 'Details' }, { num: 4, label: 'Payment' }].map((s) => (
              <div key={s.num} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      step >= s.num ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                    }`}
                    data-testid={`step-${s.num}`}
                  >
                    {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                  </div>
                  <div className="text-xs mt-2 font-medium" data-testid={`step-label-${s.num}`}>{s.label}</div>
                </div>
                {s.num < 4 && <div className={`flex-1 h-0.5 mx-4 ${step > s.num ? 'bg-primary' : 'bg-muted'}`} />}
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Step 1: Package Selection */}
            {step === 1 && (
              <div data-testid="step-package">
                <h2 className="font-playfair text-3xl font-bold mb-6" data-testid="step-title">Choose Your Package</h2>
                <div className="space-y-4">
                  {packages.map((pkg) => (
                    <Card
                      key={pkg.name}
                      className={`cursor-pointer transition-all ${
                        selectedPackage === pkg.name ? 'border-primary shadow-lg' : 'hover:shadow-md'
                      }`}
                      onClick={() => setSelectedPackage(pkg.name)}
                      data-testid={`package-option-${pkg.name}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {pkg.popular && <Badge className="mb-2">Most Popular</Badge>}
                            <h3 className="font-playfair text-2xl font-semibold mb-1">{pkg.displayName}</h3>
                            <div className="text-sm text-muted-foreground mb-4">{pkg.duration}</div>
                            <ul className="space-y-2">
                              {pkg.features.map((feature, idx) => (
                                <li key={idx} className="flex items-start text-sm">
                                  <Check className="w-4 h-4 mr-2 text-primary flex-shrink-0 mt-0.5" />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="font-playfair text-3xl font-bold">${pkg.price.toLocaleString()}</div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Date Selection */}
            {step === 2 && (
              <div data-testid="step-date">
                <h2 className="font-playfair text-3xl font-bold mb-6" data-testid="step-title">Select Your Date</h2>
                <Card>
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">Event Date</label>
                        <Input
                          type="date"
                          value={selectedDate}
                          onChange={(e) => setSelectedDate(e.target.value)}
                          data-testid="date-input"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Event Time</label>
                        <Input type="time" data-testid="time-input" />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Event Location</label>
                        <Input placeholder="Enter venue address" data-testid="location-input" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Step 3: Contact Details */}
            {step === 3 && (
              <div data-testid="step-details">
                <h2 className="font-playfair text-3xl font-bold mb-6" data-testid="step-title">Your Details</h2>
                <Card>
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">Full Name</label>
                        <Input placeholder="John Doe" data-testid="name-input" />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Email</label>
                        <Input type="email" placeholder="you@example.com" data-testid="email-input" />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Phone</label>
                        <Input type="tel" placeholder="+1 (555) 000-0000" data-testid="phone-input" />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">Special Requests (Optional)</label>
                        <textarea
                          className="flex min-h-[80px] w-full rounded-sm border-transparent bg-muted/50 px-4 py-2 text-base focus:border-primary focus:ring-1 focus:ring-primary/20 focus-visible:outline-none transition-all duration-200"
                          placeholder="Any specific requirements or preferences..."
                          data-testid="notes-input"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Step 4: Payment */}
            {step === 4 && (
              <div data-testid="step-payment">
                <h2 className="font-playfair text-3xl font-bold mb-6" data-testid="step-title">Payment</h2>
                <Card>
                  <CardContent className="p-6">
                    <div className="bg-muted/30 p-4 rounded-sm mb-6">
                      <div className="flex items-center justify-center text-yellow-600 mb-2">
                        <CreditCard className="w-5 h-5 mr-2" />
                        <span className="font-semibold">Mock Payment Mode</span>
                      </div>
                      <p className="text-sm text-center text-muted-foreground">
                        This is a demo. No actual charges will be made.
                      </p>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">Card Number</label>
                        <Input placeholder="4242 4242 4242 4242" data-testid="card-input" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium mb-2 block">Expiry Date</label>
                          <Input placeholder="MM/YY" data-testid="expiry-input" />
                        </div>
                        <div>
                          <label className="text-sm font-medium mb-2 block">CVV</label>
                          <Input placeholder="123" data-testid="cvv-input" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-8" data-testid="navigation-buttons">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={step === 1}
                data-testid="back-btn"
              >
                Back
              </Button>
              {step < 4 ? (
                <Button
                  onClick={handleNext}
                  disabled={step === 1 && !selectedPackage}
                  data-testid="next-btn"
                >
                  Next
                </Button>
              ) : (
                <Link to="/client/dashboard">
                  <Button size="lg" data-testid="confirm-btn">
                    Confirm Booking
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {/* Booking Summary Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24" data-testid="booking-summary">
              <CardHeader>
                <CardTitle>Booking Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-3 pb-4 border-b">
                  <img
                    src={photographer.image}
                    alt={photographer.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-semibold" data-testid="summary-photographer">{photographer.name}</div>
                    <div className="text-sm text-muted-foreground">Wedding Photographer</div>
                  </div>
                </div>

                {selectedPkg && (
                  <>
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">Package</div>
                      <div className="font-semibold" data-testid="summary-package">{selectedPkg.displayName}</div>
                    </div>
                    {selectedDate && (
                      <div>
                        <div className="text-sm text-muted-foreground mb-1">Date</div>
                        <div className="font-semibold" data-testid="summary-date">{selectedDate}</div>
                      </div>
                    )}
                    <div className="pt-4 border-t">
                      <div className="flex items-center justify-between mb-2">
                        <span>Package Price</span>
                        <span data-testid="summary-subtotal">${selectedPkg.price.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                        <span>Service Fee</span>
                        <span data-testid="summary-fee">$0</span>
                      </div>
                      <div className="flex items-center justify-between font-playfair text-2xl font-bold pt-4 border-t">
                        <span>Total</span>
                        <span data-testid="summary-total">${selectedPkg.price.toLocaleString()}</span>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingFlow;